import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, useFieldArray } from "react-hook-form";
import { useCallback, useContext, useEffect, useRef, useState, type FocusEvent, type MouseEvent } from "react";
import Swal from "sweetalert2";

// Icons
import { PlusIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";

// Context
import AuthContext from "../../../context/assessment/AuthContext";
import SnackbarContext from "../../../context/assessment/SnackbarContext";
import LoaderContext from "../../../context/assessment/LoaderContext";

// Controller
import Assessment from "../../../controller/assessment/assessment_system/assessment";
import Company from "../../../controller/assessment/assessment_system/company";

// Helper
import errorHandler from "../../../helper/assessment/errorHandler";

type FormValues = {
  score_name: TAssessmentScoreName[];
};

const maxScoreName = 20;

const schema = yup.object().shape({
  score_name: yup.array().of(
    yup.object().shape({
      score_name: yup.string().label("Competency").required(),
    })
  ),
});

function FormScoreName(props: {
  doc: TAssessment;
  isCreate: boolean;
  assessment_id: string;
  editable: boolean;
}) {
  const { user } = useContext(AuthContext);
  const { setMessage } = useContext(LoaderContext);
  const { setNotif } = useContext(SnackbarContext);
  const isAdmin = user.roles === "1";
  const isAllowEdit = props.doc.status === "0" || props.doc.status === "1";
  const [editing, setEditing] = useState<boolean>(false);
  const seededFromCompanyRef = useRef(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    getValues,
  } = useForm<FormValues>({
    defaultValues: { score_name: [] },
    mode: "onBlur",
    resolver: yupResolver(schema),
  });
  const { fields, append, remove } = useFieldArray({
    name: "score_name",
    control,
    keyName: "ids",
  });

  const getDoc = useCallback(
    async function getDoc() {
      try {
        if (!props.isCreate) {
          const ac = new Assessment();
          setMessage("Fetch Assessment Competency");
          const resScoreNameList = await ac.listScoreName(props.assessment_id);
          if (resScoreNameList.data.list.length > 0) {
            seededFromCompanyRef.current = true;
            reset({ score_name: resScoreNameList.data.list });
          } else if (!seededFromCompanyRef.current) {
            setMessage("Fetch Company Detail");
            const cc = new Company();
            const resCompanyCompetencyList = await cc.listCompetency(
              props.doc.company_id
            );
            const companyCompetencyList: TCompanyCompetency[] =
              resCompanyCompetencyList.data.list;
            seededFromCompanyRef.current = true;
            if (companyCompetencyList.length > 0) {
              reset({
                score_name: companyCompetencyList.map((competency) => ({
                  assessment_id: parseInt(props.assessment_id, 10),
                  score_name: competency.competency,
                })),
              });
            }
          }
        }
        setMessage("");
      } catch (error) {
        setMessage("");
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [
      setNotif,
      setMessage,
      props.assessment_id,
      props.isCreate,
      reset,
      props.doc.company_id,
    ]
  );

  useEffect(() => {
    getDoc();
  }, [getDoc]);

  const onSubmit = (data: FormValues) => {
    setEditing(false);
    void handleChange(data.score_name);
  };

  /** Jangan autosave saat masih ada baris kosong (draft) — itu yang bikin validasi “nempel” saat klik +. */
  const tryAutosave = () => {
    const values = getValues("score_name");
    if (values.some((row) => !(row.score_name ?? "").trim())) {
      return;
    }
    void handleSubmit(onSubmit)();
  };

  const onFormBlur = (event: FocusEvent<HTMLFormElement>) => {
    // Hanya simpan jika fokus benar-benar keluar dari form (bukan pindah ke Remove di dalam form)
    const next = event.relatedTarget as Node | null;
    if (next && event.currentTarget.contains(next)) {
      return;
    }
    tryAutosave();
  };

  const handleChange = async (score_names: TAssessmentScoreName[]) => {
    const previous = fields;
    try {
      for (let i = 0; i < score_names.length; i++) {
        const score_name = score_names[i];
        if (!(score_name.score_name ?? "").trim()) {
          continue;
        }
        const prev = previous[i];
        const isChanged =
          !score_name.id ||
          !prev ||
          prev.score_name !== score_name.score_name;
        if (!isChanged) continue;

        setMessage("Save Competency");
        const ac = new Assessment();
        if (score_name.id) {
          await ac.updateScoreName(score_name);
        } else {
          const resCreate = await ac.addScoreName(
            props.assessment_id,
            score_name
          );
          const current = getValues("score_name");
          const next = [...current];
          next[i] = {
            ...next[i],
            id: resCreate.data.saved_id,
          };
          setValue("score_name", next);
        }
      }
    } catch (error) {
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    } finally {
      setMessage("");
    }
  };

  const handleRemove = async (i: number) => {
    try {
      const confirm = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });
      if (confirm.isConfirmed) {
        setEditing(false);
        setMessage("Remove Competency");
        const ac = new Assessment();
        const removeScoreNameId = fields[i].id;
        if (removeScoreNameId) {
          await ac.deleteScoreName(removeScoreNameId.toString());
        }
        remove(i);
        setMessage("");
      }
    } catch (error) {
      setMessage("");
      const errorMessage = errorHandler(error);
      setNotif({ type: "error", message: errorMessage });
    }
  };

  const addCompetency = () => {
    append({
      assessment_id: parseInt(props.assessment_id, 10) || 0,
      score_name: "",
    });
    setEditing(true);
  };

  /** Cegah blur→validasi mendahului klik tombol (race saat sesi live). */
  const keepFocusForClick = (event: MouseEvent) => {
    event.preventDefault();
  };

  return (
    <div className="pb-24">
      {fields.length < maxScoreName && props.editable && isAdmin && isAllowEdit && (
        <div className="mb-2 flex flex-wrap gap-2">
          <button
            type="button"
            onMouseDown={keepFocusForClick}
            onClick={addCompetency}
            className="block w-auto rounded bg-blue-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            <div className="flex">
              <PlusIcon
                className="mr-2 h-5 w-5 stroke-white fill-tranparent"
                aria-hidden="true"
              />
              Competency
            </div>
          </button>
          {editing && (
            <button
              type="button"
              onMouseDown={keepFocusForClick}
              onClick={() => handleSubmit(onSubmit)()}
              className="block w-auto rounded bg-green-500 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
            >
              <div className="flex">
                <ArrowDownTrayIcon
                  className="mr-2 h-5 w-5 stroke-white fill-tranparent"
                  aria-hidden="true"
                />
                Save
              </div>
            </button>
          )}
        </div>
      )}
      <div className="mb-1 hidden w-full md:flex">
        <div className="w-full rounded border bg-slate-600 px-3 py-2 text-center text-white md:w-1/12">
          No
        </div>
        <div className="w-full rounded border bg-slate-600 px-3 py-2 text-white md:w-11/12">
          Competency
        </div>
        {props.editable && isAdmin && isAllowEdit && (
          <div className="w-full rounded border bg-red-500 px-3 py-2 text-center text-white md:w-1/12">
            Remove
          </div>
        )}
      </div>
      <form onBlur={onFormBlur}>
        {fields.map((_, i) => {
          return (
            <div key={`score_name_${i}`} className="mb-1 w-full md:flex">
              <div className="w-full rounded border bg-slate-600 px-3 py-2 text-white md:w-1/12 md:text-center">
                {i + 1}
              </div>
              <div className="w-full border-x border-white md:w-11/12">
                <input
                  className={
                    (errors?.score_name?.[i]?.score_name
                      ? "border-red-500"
                      : "border-gray-200 focus:border-gray-500") +
                    " block w-full appearance-none rounded border bg-gray-200 px-3 py-2 text-gray-700 focus:bg-white focus:outline-none disabled:text-gray-800"
                  }
                  disabled={!(isAdmin && isAllowEdit)}
                  {...register(`score_name.${i}.score_name`)}
                  onFocus={() => setEditing(true)}
                />
              </div>
              {props.editable && isAdmin && isAllowEdit && (
                <button
                  type="button"
                  className="w-full cursor-pointer rounded border bg-red-500 px-3 py-2 text-center text-white hover:bg-red-700 disabled:bg-slate-500 md:w-1/12"
                  onMouseDown={keepFocusForClick}
                  onClick={() => handleRemove(i)}
                  disabled={fields.length === 1}
                >
                  Remove
                </button>
              )}
            </div>
          );
        })}
      </form>
    </div>
  );
}

export default FormScoreName;
